import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { listLocations, type LocationListItem } from "@/features/locations/locations.api";
import { formatAddress } from "@/features/locations/labels";

/** A place in Locaties as box 1 of the CMR shows it. */
export type CmrSender = { id: string; code: string; name: string; city: string; address?: string };

/**
 * The fixed sender of a customer's CMRs (docs/tms-documenten.md §4.2, V28): one of the places in Locaties, found by
 * name, code or place. Empty is the loading address, as TALIS did without an *afwijkende afzender*.
 */
export function CmrSenderField({ value, onChange, disabled }: { value: CmrSender | null; onChange: (value: CmrSender | null) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [items, setItems] = useState<LocationListItem[]>([]);
  const latest = useRef(0);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setItems([]);
      return;
    }
    const request = ++latest.current;
    const timer = setTimeout(() => {
      listLocations({ q, isActive: "true" })
        .then((page) => {
          if (request === latest.current) setItems(page.data.slice(0, 8));
        })
        .catch(() => {
          if (request === latest.current) setItems([]);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  return (
    <div className="wl-field">
      <label className="wl-field__label" htmlFor="customer-cmrSender">
        {t("parties.cmrSenderLabel")}
      </label>
      {value ? (
        <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center" }}>
          <span className="t-body-sm">
            {value.name} <span className="t-mono t-caption t-muted">{value.code}</span>
          </span>
          <span className="t-caption t-muted">{value.address ?? value.city}</span>
          <span className="wl-spacer" />
          {!disabled && (
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onChange(null)}>
              {t("parties.cmrSenderClear")}
            </button>
          )}
        </div>
      ) : disabled ? (
        <span className="t-body-sm t-muted">{t("parties.cmrSenderNone")}</span>
      ) : (
        <>
          <input
            id="customer-cmrSender"
            className="wl-input"
            value={text}
            autoComplete="off"
            placeholder={t("orders.placeSearchPlaceholder")}
            onChange={(e) => setText(e.target.value)}
          />
          {items.length > 0 && (
            <ul className="wl-vstack" style={{ listStyle: "none", margin: 0, padding: 0, gap: "var(--space-0_5)", alignItems: "stretch" }}>
              {items.map((location) => (
                <li key={location.id}>
                  <button
                    type="button"
                    className="wl-btn wl-btn--ghost wl-btn--sm"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left", height: "auto", padding: "var(--space-1_5) var(--space-2)" }}
                    onClick={() => {
                      onChange({ id: location.id, code: location.code, name: location.name, city: location.city, address: formatAddress(location) });
                      setText("");
                      setItems([]);
                    }}
                  >
                    <span className="wl-vstack" style={{ gap: "var(--space-0_5)", alignItems: "flex-start" }}>
                      <span className="t-body-sm">
                        {location.name} <span className="t-mono t-caption t-muted">{location.code}</span>
                      </span>
                      <span className="t-caption t-muted">{formatAddress(location)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <span className="wl-field__help">{t("parties.cmrSenderHelp")}</span>
    </div>
  );
}
